import React, { useState, useEffect } from 'react';
import { useLazyLoading } from '../../hooks/useLazyLoading';
import { supportsWebP, getOptimizedImageUrl, createSrcSet } from '../../utils/imageOptimization';
import { ImageAssetType } from './image';
import './lazyImage.css';

interface LazyImageProps {
    type?: ImageAssetType;
    className: string;
    style?: React.CSSProperties;
    alt: string;
    src: string;
    placeholder?: string;
    threshold?: number;
    rootMargin?: string;
    sizes?: string;
    loading?: 'lazy' | 'eager';
    width?: number;
    height?: number;
    quality?: number;
    enableWebP?: boolean;
}

const URL = (process.env.REACT_APP_IMAGE_URL||"").replace("http://", "https://");

function getImageUrl(type: string, src: string) {
    return URL + type + '/' + src;
}

export function LazyImage(props: LazyImageProps) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [webPSupported, setWebPSupported] = useState(false);
    
    const { elementRef, isInView } = useLazyLoading({
        threshold: props.threshold || 0.1,
        rootMargin: props.rootMargin || '50px',
        triggerOnce: true
    });

    useEffect(() => {
        if (props.enableWebP) {
            supportsWebP().then(setWebPSupported);
        }
    }, [props.enableWebP]);

    useEffect(() => {
        setIsLoaded(false);
        setHasError(false);
    }, [props.src]);

    const handleLoad = () => {
        setIsLoaded(true);
    };

    const handleError = () => {
        setHasError(true);
    };

    const getImageSrc = () => {
        if (!isInView) return props.placeholder || '';
        
        if (props.src) {
            if (props.type) {
                return getOptimizedImageUrl(URL, props.type, props.src, {
                    width: props.width,
                    height: props.height,
                    quality: props.quality,
                    format: props.enableWebP && webPSupported ? 'webp' : undefined
                });
            }
            return props.src;
        }
        return props.placeholder || '';
    };

    const getSrcSet = () => {
        if (!isInView || !props.src || !props.type) return undefined;
        
        return createSrcSet(URL, props.type, props.src);
    };

    const shouldRenderImage = isInView && !hasError;
    const shouldShowLoader = !hasError && !isLoaded;

    return (
        <div 
            ref={elementRef as React.RefObject<HTMLDivElement>}
            className={`lazy-image-container ${props.className}`}
            style={props.style}
        >
            {shouldRenderImage && (
                <img
                    alt={props.alt || ""}
                    src={getImageSrc()}
                    srcSet={getSrcSet()}
                    onLoad={handleLoad}
                    onError={handleError}
                    className="lazy-image-img"
                    loading={props.loading || 'lazy'}
                    sizes={props.sizes}
                    width={props.width}
                    height={props.height}
                    style={{
                        opacity: isLoaded && !hasError ? 1 : 0
                    }}
                />
            )}
            
            {shouldShowLoader && (
                <div className="lazy-image-loading">
                    <div className="lazy-image-spinner" />
                </div>
            )}

            {hasError && (
                <div className="lazy-image-error">
                    {props.alt || 'Image'}
                </div>
            )}
        </div>
    );
}
